class ImageCarouselComponent extends HTMLElement {
    constructor() {
        super();
        this.currentIndex = 0;
        this.itemsToShow = 1;
        this.totalItems = 0;
        this.originalTotalItems = 0;
        this.cloneCount = 0;
        this.isTransitioning = false;
        this.startX = 0;
        this.currentX = 0;
        this.isDragging = false;
        this.currentColumns = 1;

        // Auto-rotation properties
        this.autoRotate = false;
        this.autoRotateSpeed = 4000;
        this.infiniteLoop = true;
        this.autoRotateInterval = null;

        this.init();
    }

    init() {
        this.carousel = this.querySelector('[data-carousel]');
        this.prevButton = this.querySelector('.image-carousel__nav--prev');
        this.nextButton = this.querySelector('.image-carousel__nav--next');
        this.slides = this.querySelectorAll('.image-carousel__slide');

        if (!this.carousel || this.slides.length === 0) return;

        this.originalTotalItems = this.slides.length;
        this.setupSettings();
        this.setupInfiniteLoop();

        // Agregar transición CSS inicial
        this.carousel.style.transition = 'transform 0.6s ease';

        this.setupResponsive();
        this.setupNavigation();
        this.updateCarousel();
        this.setupKeyboardNavigation();
        this.setupTouchNavigation();
        this.setupAutoRotation();
    }

    setupSettings() {
        const section = this.closest('[data-auto-rotate]');
        if (section) {
            this.autoRotate = section.dataset.autoRotate === 'true';
            this.autoRotateSpeed = (parseInt(section.dataset.autoRotateSpeed) || 4) * 1000;
            this.infiniteLoop = section.dataset.infiniteLoop === 'true';
        }
    }

    setupInfiniteLoop() {
        if (!this.infiniteLoop || this.originalTotalItems <= 1) {
            this.totalItems = this.originalTotalItems;
            return;
        }

        // Clonar elementos para scroll infinito suave
        const originalSlides = Array.from(this.slides);

        // Clonar al final del carrusel (los primeros elementos)
        originalSlides.forEach((slide, index) => {
            const clone = slide.cloneNode(true);
            clone.classList.add('image-carousel__slide--clone-end');
            clone.setAttribute('aria-hidden', 'true');
            this.carousel.appendChild(clone);
        });

        // Clonar al principio del carrusel (los últimos elementos)
        for (let i = originalSlides.length - 1; i >= 0; i--) {
            const clone = originalSlides[i].cloneNode(true);
            clone.classList.add('image-carousel__slide--clone-start');
            clone.setAttribute('aria-hidden', 'true');
            this.carousel.insertBefore(clone, this.carousel.firstChild);
        }

        // Actualizar referencias
        this.slides = this.querySelectorAll('.image-carousel__slide');
        this.totalItems = this.slides.length;
        this.cloneCount = this.originalTotalItems;

        // Establecer posición inicial (después de los clones del principio)
        this.currentIndex = this.cloneCount;
    }

    setupResponsive() {
        const updateColumns = () => {
            const section = this.closest('[data-columns-desktop]');
            if (!section) return;

            const desktopColumns = parseInt(section.dataset.columnsDesktop) || 4;
            const tabletColumns = parseInt(section.dataset.columnsTablet) || 2;
            const mobileColumns = parseInt(section.dataset.columnsMobile) || 1;

            if (window.innerWidth >= 990) {
                this.itemsToShow = desktopColumns;
            } else if (window.innerWidth >= 750) {
                this.itemsToShow = tabletColumns;
            } else {
                this.itemsToShow = mobileColumns;
            }

            this.currentColumns = this.itemsToShow;
            this.updateSlideWidths();
            this.updateCarousel();
        };

        updateColumns();
        window.addEventListener('resize', updateColumns);
    }

    updateSlideWidths() {
        const width = window.innerWidth;
        let gapInRem, gapInPx;

        if (width >= 990) {
            gapInRem = 1.2;
            gapInPx = gapInRem * 16;
            const totalGap = gapInPx * (this.currentColumns - 1);
            const availableWidth = this.carousel.offsetWidth - totalGap;
            const slideWidth = availableWidth / this.currentColumns;

            this.slides.forEach(slide => {
                slide.style.width = `${slideWidth}px`;
                slide.style.flex = `0 0 ${slideWidth}px`;
            });
        } else if (width >= 750) {
            gapInRem = 1.5;
            const slideWidth = 100 / this.currentColumns;
            this.slides.forEach(slide => {
                slide.style.width = `calc((100% - ${gapInRem * (this.currentColumns - 1)}rem) / ${this.currentColumns})`;
                slide.style.flex = `0 0 calc((100% - ${gapInRem * (this.currentColumns - 1)}rem) / ${this.currentColumns})`;
            });
        } else {
            const slideWidth = 100 / this.currentColumns;
            this.slides.forEach(slide => {
                slide.style.width = `${slideWidth}%`;
                slide.style.flex = `0 0 ${slideWidth}%`;
            });
        }
    }

    setupNavigation() {
        if (this.prevButton) {
            this.prevButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.previousSlide();
            });
        }

        if (this.nextButton) {
            this.nextButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.nextSlide();
            });
        }
    }

    setupAutoRotation() {
        if (!this.autoRotate || this.originalTotalItems <= this.itemsToShow) return;

        this.startAutoRotation();

        this.addEventListener('mouseenter', () => this.stopAutoRotation());
        this.addEventListener('mouseleave', () => this.startAutoRotation());
        this.addEventListener('focusin', () => this.stopAutoRotation());
        this.addEventListener('focusout', () => this.startAutoRotation());
    }

    startAutoRotation() {
        this.stopAutoRotation();
        if (this.autoRotate && this.originalTotalItems > this.itemsToShow) {
            this.autoRotateInterval = setInterval(() => {
                this.nextSlide(true);
            }, this.autoRotateSpeed);
        }
    }

    stopAutoRotation() {
        if (this.autoRotateInterval) {
            clearInterval(this.autoRotateInterval);
            this.autoRotateInterval = null;
        }
    }

    setupTouchNavigation() {
        if (!this.carousel) return;

        let startX = 0;
        let startY = 0;
        let currentX = 0;
        let hasMoved = false;
        let isHorizontalSwipe = false;

        this.carousel.addEventListener('touchstart', (e) => {
            if (this.originalTotalItems <= 1) return;
            this.stopAutoRotation();
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            currentX = startX;
            hasMoved = false;
            isHorizontalSwipe = false;
        }, { passive: true });

        this.carousel.addEventListener('touchmove', (e) => {
            if (this.originalTotalItems <= 1) return;

            currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;
            const deltaX = Math.abs(currentX - startX);
            const deltaY = Math.abs(currentY - startY);

            if (deltaX > 10 || deltaY > 10) {
                isHorizontalSwipe = deltaX > deltaY;

                if (isHorizontalSwipe && deltaX > 20) {
                    hasMoved = true;
                    e.preventDefault();
                }
            }
        }, { passive: false });

        this.carousel.addEventListener('touchend', (e) => {
            if (this.originalTotalItems <= 1) return;

            const deltaX = startX - currentX;
            const absDeltaX = Math.abs(deltaX);

            if (hasMoved && isHorizontalSwipe && absDeltaX > 30) {
                if (deltaX > 0) {
                    this.nextSlide();
                } else {
                    this.previousSlide();
                }
                e.preventDefault();
                e.stopPropagation();
            }

            setTimeout(() => this.startAutoRotation(), 3000);
            startX = 0;
            currentX = 0;
            hasMoved = false;
            isHorizontalSwipe = false;
        }, { passive: false });

        let mouseStartX = 0;
        let mouseHasMoved = false;

        this.carousel.addEventListener('mousedown', (e) => {
            if (this.originalTotalItems <= 1 || window.innerWidth < 750) return;
            this.stopAutoRotation();
            mouseStartX = e.clientX;
            mouseHasMoved = false;
            e.preventDefault();
        });

        document.addEventListener('mousemove', (e) => {
            if (mouseStartX === 0 || window.innerWidth < 750) return;

            const deltaX = Math.abs(e.clientX - mouseStartX);
            if (deltaX > 20) {
                mouseHasMoved = true;
                this.carousel.style.cursor = 'grabbing';
            }
        });

        document.addEventListener('mouseup', (e) => {
            if (mouseStartX === 0 || window.innerWidth < 750) {
                mouseStartX = 0;
                return;
            }

            const deltaX = mouseStartX - e.clientX;
            const absDeltaX = Math.abs(deltaX);

            if (mouseHasMoved && absDeltaX > 30) {
                if (deltaX > 0) {
                    this.nextSlide();
                } else {
                    this.previousSlide();
                }
            }

            setTimeout(() => this.startAutoRotation(), 3000);
            mouseStartX = 0;
            mouseHasMoved = false;
            this.carousel.style.cursor = '';
        });

        this.carousel.addEventListener('dragstart', (e) => {
            e.preventDefault();
        });
    }

    setupKeyboardNavigation() {
        this.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.stopAutoRotation();
                this.previousSlide();
                setTimeout(() => this.startAutoRotation(), 3000);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.stopAutoRotation();
                this.nextSlide();
                setTimeout(() => this.startAutoRotation(), 3000);
            }
        });
    }

    previousSlide(isAutoRotation = false) {
        if (this.isTransitioning) return;

        this.currentIndex--;
        this.updateCarousel();

        if (this.infiniteLoop) {
            this.checkInfiniteLoop();
        }
    }

    nextSlide(isAutoRotation = false) {
        if (this.isTransitioning) return;

        this.currentIndex++;
        this.updateCarousel();

        if (this.infiniteLoop) {
            this.checkInfiniteLoop();
        }
    }

    checkInfiniteLoop() {
        if (!this.infiniteLoop || this.originalTotalItems <= 1) return;

        // Si estamos mostrando los clones del final, reposicionar al inicio real
        if (this.currentIndex >= this.cloneCount + this.originalTotalItems) {
            setTimeout(() => {
                this.carousel.style.transition = 'none';
                this.currentIndex = this.cloneCount;
                this.updateCarousel(true); // true = sin transición
                setTimeout(() => {
                    this.carousel.style.transition = 'transform 0.6s ease';
                }, 50);
            }, 600);
        }

        // Si estamos mostrando los clones del principio, reposicionar al final real
        if (this.currentIndex < this.cloneCount) {
            setTimeout(() => {
                this.carousel.style.transition = 'none';
                this.currentIndex = this.cloneCount + this.originalTotalItems - this.itemsToShow;
                this.updateCarousel(true); // true = sin transición
                setTimeout(() => {
                    this.carousel.style.transition = 'transform 0.6s ease';
                }, 50);
            }, 600);
        }
    }

    updateCarousel(skipTransition = false) {
        if (!this.carousel) return;

        if (!this.infiniteLoop) {
            const maxIndex = Math.max(0, this.originalTotalItems - this.itemsToShow);
            this.currentIndex = Math.min(this.currentIndex, maxIndex);
            this.currentIndex = Math.max(0, this.currentIndex);
        }

        let slideWidth = 100 / this.itemsToShow;
        let translateX = -(this.currentIndex * slideWidth);

        if (!skipTransition) {
            this.isTransitioning = true;
        }

        this.carousel.style.transform = `translateX(${translateX}%)`;

        this.updateSlideWidths();
        this.updateNavigationButtons();

        if (!skipTransition) {
            setTimeout(() => {
                this.isTransitioning = false;
            }, 600);
        }
    }

    updateNavigationButtons() {
        const isMobile = window.innerWidth < 750;

        if (isMobile) {
            if (this.prevButton) {
                this.prevButton.style.display = 'none';
            }
            if (this.nextButton) {
                this.nextButton.style.display = 'none';
            }
            return;
        }

        // En modo infinito, los botones siempre están habilitados
        if (this.infiniteLoop) {
            if (this.prevButton) {
                this.prevButton.disabled = false;
                this.prevButton.style.opacity = '1';
                this.prevButton.style.display = 'flex';
                this.prevButton.setAttribute('aria-disabled', 'false');
            }

            if (this.nextButton) {
                this.nextButton.disabled = false;
                this.nextButton.style.opacity = '1';
                this.nextButton.style.display = 'flex';
                this.nextButton.setAttribute('aria-disabled', 'false');
            }
        } else {
            const maxIndex = Math.max(0, this.originalTotalItems - this.itemsToShow);

            if (this.prevButton) {
                const shouldDisablePrev = this.currentIndex === 0;
                this.prevButton.disabled = shouldDisablePrev;
                this.prevButton.style.opacity = shouldDisablePrev ? '0.3' : '1';
                this.prevButton.style.display = 'flex';
                this.prevButton.setAttribute('aria-disabled', shouldDisablePrev);
            }

            if (this.nextButton) {
                const shouldDisableNext = this.currentIndex >= maxIndex;
                this.nextButton.disabled = shouldDisableNext;
                this.nextButton.style.opacity = shouldDisableNext ? '0.3' : '1';
                this.nextButton.style.display = 'flex';
                this.nextButton.setAttribute('aria-disabled', shouldDisableNext);
            }
        }
    }

    goToSlide(index) {
        if (this.isTransitioning) return;

        if (this.infiniteLoop) {
            // Ajustar el índice para contar los clones del principio
            this.currentIndex = this.cloneCount + Math.max(0, Math.min(index, this.originalTotalItems - this.itemsToShow));
        } else {
            const maxIndex = Math.max(0, this.originalTotalItems - this.itemsToShow);
            this.currentIndex = Math.max(0, Math.min(index, maxIndex));
        }

        this.updateCarousel();
    }

    disconnectedCallback() {
        this.stopAutoRotation();
    }
}

if (!customElements.get('image-carousel-component')) {
    customElements.define('image-carousel-component', ImageCarouselComponent);
}

document.addEventListener('DOMContentLoaded', () => {
    const carousels = document.querySelectorAll('image-carousel-component');
    carousels.forEach(carousel => {
        if (!carousel.initialized) {
            carousel.initialized = true;
        }
    });
});
